import { memberApiClient } from './memberApiClient.js';
import { convertToApiFormat } from '../utils/paymentMethodUtils';

// バリデーション用の定数
const VALIDATION_CONSTANTS = {
  // 必須項目の定義
  REQUIRED_FIELDS: [
    { key: 'shop', label: '店舗' },
    { key: 'name', label: '名前' },
    { key: 'nameKana', label: 'ひらがな名前' }, 
    { key: 'email', label: 'メールアドレス' },
    { key: 'phoneNumber', label: '電話番号' }, 
    { key: 'password', label: 'パスワード' },
    { key: 'passwordConfirm', label: 'パスワード確認' },
    { key: 'postalCode', label: '郵便番号' },
    { key: 'prefecture', label: '都道府県' },
    { key: 'city', label: '市区町村' },
    { key: 'town', label: '住所' },
    { key: 'birthday', label: '生年月日' },
    { key: 'desiredDate', label: '利用開始日' }
  ],

  // 正規表現パターン
  REGEX: {
    HIRAGANA: /^[あ-んー\s　]*$/,
    PHONE: /^(0\d{1,4}-?\d{1,4}-?\d{4}|070-?\d{4}-?\d{4}|080-?\d{4}-?\d{4}|090-?\d{4}-?\d{4})$/,
    EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  }
};

/**
 * 会員登録ビジネスロジック
 */
class MemberService {
  constructor(apiClient = memberApiClient) {
    this.apiClient = apiClient;
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  /**
   * フォームデータをAPI形式に変換
   * @param {Object} formData - RegisterFormのformData
   * @returns {Object} API送信用データ
   */
  _transformFormDataToApiRequest(formData) {
    return {
      // 必須項目
      storeName: formData.shop,
      fullName: formData.name,
      fullNameKana: formData.nameKana, 
      email: formData.email.toLowerCase().trim(),
      password: formData.password,
      passwordConfirm: formData.passwordConfirm,
      phoneNumber: formData.phoneNumber || formData.emergencyTel,
      postalCode: this._formatPostalCode(formData.postalCode),
      prefecture: formData.prefecture,
      city: formData.city,
      address: formData.town,
      birthDate: formData.birthday,
      serviceStartDate: formData.desiredDate,
      paymentMethod: this._convertPaymentMethod(formData.paymentMethod),

      // 任意項目
      gender: formData.gender,
      buildingRoom: formData.building,
      emergencyContactName: formData.emergencyName,
      emergencyContactPhone: formData.emergencyTel,
      additionalStores: formData.additionalShops?.length > 0 
    ? formData.additionalShops.join(',') 
    : null,
      shoesLockerApplication: this._convertShoesLockerOption(formData.shoeLocker)
    };
  }

  /**
   * 郵便番号をAPI形式にフォーマット
   * @param {string} postalCode - 7桁の郵便番号
   * @returns {string} "123-4567"形式
   */
  _formatPostalCode(postalCode) {
    if (!postalCode || postalCode.length !== 7) return postalCode;
    return `${postalCode.slice(0, 3)}-${postalCode.slice(3)}`;
  }

  /**
   * 電話番号をAPI形式にフォーマット
   * @param {string} phoneNumber - 電話番号
   * @returns {string} ハイフン付きの統一形式
   */
  _formatPhoneNumber(phoneNumber) {
    // 早期リターン：無効な入力の場合
    if (!phoneNumber) return phoneNumber;
    
    // 数字のみ抽出
    const numbersOnly = phoneNumber.replace(/[^0-9]/g, '');
    
    // 携帯電話の場合（11桁）
    if (numbersOnly.length === 11) {
      return `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3, 7)}-${numbersOnly.slice(7)}`;
    }
    
    // 固定電話の場合（10桁）
    if (numbersOnly.length === 10 && numbersOnly.startsWith('0')) {
      return this._formatLandlineNumber(numbersOnly);
    }
    
    // その他の場合はそのまま返す
    return phoneNumber;
  }

  /**
   * 固定電話番号のフォーマット（10桁専用）
   * @param {string} numbersOnly - 10桁の数字文字列（0から開始）
   * @returns {string} フォーマットされた固定電話番号
   */
  _formatLandlineNumber(numbersOnly) {
    // 2桁市外局番のプレフィックス
    const twoDigitPrefixes = ['03', '04', '06'];
    
    // 2桁市外局番かどうかチェック
    const hasTwoDigitPrefix = twoDigitPrefixes.some(prefix => 
      numbersOnly.startsWith(prefix)
    );
    
    if (hasTwoDigitPrefix) {
      // 2桁市外局番: 03-1234-5678
      return `${numbersOnly.slice(0, 2)}-${numbersOnly.slice(2, 6)}-${numbersOnly.slice(6)}`;
    } else {
      // 3桁市外局番: 045-123-4567
      return `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3, 6)}-${numbersOnly.slice(6)}`;
    }
  }

  /**
   * 支払い方法をAPI形式に変換
   * @param {string} paymentMethod - フォームの値
   * @returns {string} API用の値
   */
  _convertPaymentMethod(paymentMethod) {
    return convertToApiFormat(paymentMethod);
  }

  /**
   * シューズロッカー選択をAPI形式に変換
   * @param {string} shoeLocker - フォームの値
   * @returns {boolean} API用の値
   */
  _convertShoesLockerOption(shoeLocker) {
    return shoeLocker === '申込する';
  }

  /**
   * フォームデータの検証
   * @param {Object} formData - 検証するデータ
   * @returns {Object} 検証結果
   */
  _validateFormData(formData) {
    const addError = (condition, message) => condition ? [message] : [];
    
    const errors = [
      // 必須項目チェック
      ...VALIDATION_CONSTANTS.REQUIRED_FIELDS
        .filter(field => !formData[field.key]?.trim())
        .map(field => `${field.label}は必須です`),
      
      // 各種バリデーション
      ...addError(formData.nameKana && !VALIDATION_CONSTANTS.REGEX.HIRAGANA.test(formData.nameKana), 
                'ひらがな名前はひらがなで入力してください'),
      ...addError(formData.phoneNumber && !VALIDATION_CONSTANTS.REGEX.PHONE.test(formData.phoneNumber), 
                '電話番号の形式が正しくありません'),
      ...addError(formData.password !== formData.passwordConfirm, 
                'パスワードと確認用パスワードが一致しません'),
      ...addError(formData.password && formData.password.length < 8, 
                'パスワードは8文字以上で入力してください'),
      ...addError(formData.email && !VALIDATION_CONSTANTS.REGEX.EMAIL.test(formData.email), 
                'メールアドレスの形式が正しくありません')
    ];
    return { isValid: errors.length === 0, errors };
  }

  /**
   * 会員登録処理
   * @param {Object} formData - RegisterFormのformData
   * @returns {Promise<Object>} 登録結果
   */
  async registerMember(formData) {
    try {

      // バリデーション
      const validation = this._validateFormData(formData);
      if (!validation.isValid) {
        console.error('[MEMBER SERVICE] Validation failed:', validation.errors);
        return {
          success: false,
          error: validation.errors.join(', '),
          type: 'validation'
        };
      }

      // データ変換
      const apiData = this._transformFormDataToApiRequest(formData);

      // バックエンドに統合登録リクエストを送信
      // バックエンドでFirebaseユーザー作成 + 会員データ登録を一括処理
      const result = await this.apiClient.registerMember(apiData);

      if (result.success) {
        return {
          success: true,
          data: result.member || result.data, // APIクライアントの結果に合わせて調整
          message: result.message || '会員登録が完了しました'
        };
      } else {
        console.error('[MEMBER SERVICE] Registration failed:', result.error);
        return {
          success: false,
          error: result.error || '会員登録に失敗しました',
          type: 'api'
        };
      }

    } catch (error) {
      console.error('[MEMBER SERVICE] Registration failed with exception:', error);

      // エラーの種類に応じた処理
      let errorMessage = error.message || '会員登録に失敗しました';
      let errorType = 'api';

      if (error.message?.includes('認証')) {
        errorType = 'auth';
        errorMessage = '認証エラーが発生しました。再度ログインしてください。';
      } else if (error.message?.includes('CSRF') || error.message?.includes('セキュリティ')) {
        errorType = 'csrf';
        errorMessage = 'セキュリティ検証エラーが発生しました。再度お試しください。';
      } else if (error.message?.includes('ネットワーク') || error.message?.includes('timeout')) {
        errorType = 'network';
        errorMessage = 'ネットワークエラーが発生しました。接続を確認してください。';
      } else if (error.message?.includes('既に使用されています') || error.message?.includes('already in use')) {
        errorType = 'duplicate';
        errorMessage = 'このメールアドレスは既に使用されています。';
      }

      return {
        success: false,
        error: errorMessage,
        type: errorType,
        originalError: error.message // デバッグ用
      };
    }
  }

  /**
   * 開発環境用のモック登録
   * @param {Object} formData - フォームデータ
   * @returns {Promise<Object>} モック結果
   */
  async _mockRegisterMember(formData) {
    // 開発環境用の遅延
    await new Promise(resolve => setTimeout(resolve, 1500));

    return {
      success: true,
      data: {
        memberId: `M${Date.now()}`,
        customerId: `CUST${Date.now()}`,
        customerCardId: `CARD${Date.now()}`,
        initialFee: 4400,
        registeredAt: new Date().toLocaleDateString('sv-SE'),
        firebaseUid: `firebase_${Date.now()}` // バックエンドで作成されたFirebase UID
      },
      message: '会員登録が完了しました（開発環境）'
    };
  }
}

// 関数スタイルのエクスポート
export const memberService = new MemberService();

export const registerMember = (formData) => memberService.registerMember(formData);

export default MemberService;